// Offline Banner Component
// Displays a banner when the app is offline or has pending sync items

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff, CloudOff, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { formatDistanceToNow } from 'date-fns';
import { fr, de, enUS, it } from 'date-fns/locale';

const DATE_LOCALES: Record<string, typeof fr> = { fr, de, en: enUS, it };

interface OfflineBannerProps {
  className?: string;
  showSyncStatus?: boolean;
  onRetrySync?: () => void;
}

type BannerState = 'online' | 'offline' | 'syncing' | 'reconnecting';

export function OfflineBanner({
  className = '',
  showSyncStatus = true,
  onRetrySync,
}: OfflineBannerProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0] ?? 'fr'] ?? fr;
  const {
    isOnline,
    isOffline,
    lastOnlineAt,
    lastSyncAt,
    syncQueueCount,
  } = useOfflineStatus();

  const [bannerState, setBannerState] = useState<BannerState>('online');
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);

  // Update banner state based on connection status
  useEffect(() => {
    if (isOffline) {
      setBannerState('offline');
      setShowBanner(true);
      setJustReconnected(false);
    } else if (isReconnecting) {
      setBannerState('reconnecting');
    } else if (syncQueueCount > 0) {
      setBannerState('syncing');
      setShowBanner(true);
    } else {
      setBannerState('online');
      // Keep banner visible briefly when reconnecting
      if (showBanner && !justReconnected) {
        setJustReconnected(true);
        setTimeout(() => {
          setShowBanner(false);
          setJustReconnected(false);
        }, 3000);
      }
    }
  }, [isOffline, isReconnecting, syncQueueCount, showBanner, justReconnected]);

  // Listen for online event to show reconnecting animation
  useEffect(() => {
    const handleOnline = () => {
      setIsReconnecting(true);
      setTimeout(() => {
        setIsReconnecting(false);
      }, 2000);
    };

    window.addEventListener('app:online', handleOnline);
    return () => window.removeEventListener('app:online', handleOnline);
  }, []);

  // Don't render if online and no pending sync
  if (!showBanner && isOnline && syncQueueCount === 0) {
    return null;
  }

  const getBannerContent = () => {
    switch (bannerState) {
      case 'offline':
        return {
          icon: WifiOff,
          message: t('offline_banner.offline_title'),
          subMessage: lastOnlineAt
            ? t('offline_banner.offline_last_connection', {
                time: formatDistanceToNow(lastOnlineAt, { addSuffix: true, locale: dateLocale }),
              })
            : t('offline_banner.offline_no_connection'),
          bgColor: 'bg-amber-500',
          textColor: 'text-amber-950',
          iconColor: 'text-amber-950',
        };

      case 'reconnecting':
        return {
          icon: RefreshCw,
          message: t('offline_banner.reconnecting_title'),
          subMessage: t('offline_banner.reconnecting_subtitle'),
          bgColor: 'bg-blue-500',
          textColor: 'text-white',
          iconColor: 'text-white',
          animate: true,
        };

      case 'syncing':
        return {
          icon: CloudOff,
          message: t('offline_banner.syncing_actions', { count: syncQueueCount }),
          subMessage: lastSyncAt
            ? t('offline_banner.syncing_last_sync', {
                time: formatDistanceToNow(lastSyncAt, { addSuffix: true, locale: dateLocale }),
              })
            : t('offline_banner.syncing_pending'),
          bgColor: 'bg-orange-500',
          textColor: 'text-orange-950',
          iconColor: 'text-orange-950',
        };

      case 'online':
      default:
        return {
          icon: Check,
          message: t('offline_banner.online_title'),
          subMessage: t('offline_banner.online_subtitle'),
          bgColor: 'bg-green-500',
          textColor: 'text-white',
          iconColor: 'text-white',
        };
    }
  };

  const content = getBannerContent();
  const Icon = content.icon;

  return (
    <div
      className={`
        ${content.bgColor} ${content.textColor}
        px-4 py-2 flex items-center justify-between
        transition-all duration-300 ease-in-out
        ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        <Icon
          className={`
            w-5 h-5 ${content.iconColor}
            ${content.animate ? 'animate-spin' : ''}
          `}
        />
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
          <span className="font-medium text-sm">{content.message}</span>
          <span className="text-xs opacity-80 hidden sm:inline">
            {content.subMessage}
          </span>
        </div>
      </div>

      {/* Retry button for syncing state */}
      {showSyncStatus && bannerState === 'syncing' && onRetrySync && (
        <button
          onClick={onRetrySync}
          className={`
            px-3 py-1 text-xs font-medium rounded
            bg-white/20 hover:bg-white/30
            transition-colors duration-200
          `}
        >
          {t('offline_banner.retry')}
        </button>
      )}

      {/* Connection quality indicator for offline */}
      {bannerState === 'offline' && (
        <div className="flex items-center gap-1">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-xs font-medium hidden sm:inline">
            {t('offline_banner.offline_cache_available')}
          </span>
        </div>
      )}
    </div>
  );
}

// Compact version for use in headers
export function OfflineIndicator({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const { isOffline, syncQueueCount } = useOfflineStatus();

  if (!isOffline && syncQueueCount === 0) {
    return null;
  }

  return (
    <div
      className={`
        flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
        ${isOffline ? 'bg-amber-500/20 text-amber-600' : 'bg-orange-500/20 text-orange-600'}
        ${className}
      `}
      title={isOffline
        ? t('offline_banner.indicator_offline_title')
        : t('offline_banner.indicator_pending_title', { count: syncQueueCount })}
    >
      {isOffline ? (
        <>
          <WifiOff className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t('offline_banner.indicator_offline')}</span>
        </>
      ) : (
        <>
          <CloudOff className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{syncQueueCount}</span>
        </>
      )}
    </div>
  );
}

export default OfflineBanner;
